import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useUrlSyncedId } from '../../hooks/useUrlSyncedId';
import {
  useColorMode,
  Text,
  Button,
  VStack,
  Spinner,
  Center,
  useToast,
  useDisclosure,
  Input,
  Flex,
  Box,
  InputGroup,
  InputLeftElement,
  IconButton,
  SimpleGrid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody
} from '@chakra-ui/react';
import { AddIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { LuLayers } from 'react-icons/lu';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import PageHeader from '../shared/PageHeader';
import Figure from '../shared/Figure';
import { cardSurface } from '../shared/Card';
import { chartColor } from '../shared/TimeSeriesChart';
import { categoryTint } from '../../utils/categoryTint';
import { fromMonthParam, toMonthParam } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { deleteCategory, fetchTags } from '../../api/endpointsBackend';
import { useCategoriesView } from '../../hooks/useCategoriesView';
import DashboardCurrencyTabs from '../dashboard/DashboardCurrencyTabs';
import DisplayCurrencySelector from '../shared/DisplayCurrencySelector';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import CategoryDetailPanel from './CategoryDetailPanel';
import AllCategoriesDetailPanel from './AllCategoriesDetailPanel';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneCategories from '../phone/PhoneCategories';

import { useResizablePanel } from '../../hooks/useResizablePanel';
import PanelResizeHandle, { pagePanelOptions, resizablePanelWidth } from '../shared/PanelResizeHandle';
export const DEFAULT_CATEGORY_COLOR = '#ED682D';
export const FALLBACK_CATEGORY_COLOR = '#718096';

// Copilot Money-inspired palette (16 sampled colors) + 4 complementary
// tones (cyan, soft rose, and two neutrals) to keep 20 options.
export const CATEGORY_COLORS = [
  // Warm
  '#EB3A2E', '#ED682D', '#E78532', '#E7C041', '#B27029',
  // Greens & aqua
  '#8BC34A', '#2E9E5B', '#44942F', '#4EB0B1', '#0BC5EA',
  // Cool
  '#3F8DF7', '#5755F6', '#6478A5', '#9C45F6', '#C63EF6',
  // Pink & neutral
  '#EB41D3', '#EB3862', '#F19EC2', '#2D3748', '#A0AEC0',
];

export const ColorPicker = ({ value, onChange }) => {
  const displayColor = value || DEFAULT_CATEGORY_COLOR;

  return (
    <Popover placement="bottom-start" strategy="fixed">
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Flex
              h="40px"
              align="center"
              cursor="pointer"
              _hover={{ opacity: 0.8 }}
              transition="all 0.15s"
              gap={1}
            >
              <Box
                w="24px"
                h="24px"
                borderRadius="full"
                bg={displayColor}
                border="2px solid"
                borderColor="redesign.border"
                flexShrink={0}
              />
              <Text fontSize="xs" color="redesign.text.muted">▾</Text>
            </Flex>
          </PopoverTrigger>
          <PopoverContent w="fit-content" borderRadius="lg">
            <PopoverBody p={3}>
              <SimpleGrid columns={5} spacing={2}>
                {CATEGORY_COLORS.map((color) => (
                  <Box
                    key={color}
                    w="28px"
                    h="28px"
                    borderRadius="full"
                    bg={color}
                    cursor="pointer"
                    onClick={() => {
                      onChange(value === color ? '' : color);
                      onClose();
                    }}
                    border="2px solid"
                    borderColor={value === color ? 'redesign.heading' : 'transparent'}
                    _hover={{ transform: 'scale(1.15)' }}
                    transition="all 0.15s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {value === color && (
                      <Text fontSize="xs" color="white" fontWeight="bold">✓</Text>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

const PANEL_CREATE_MODE = 'new';
const PANEL_ALL_MODE = 'all';

function CurrencyTotalsBlock({ totalsByCurrency, label, align = 'left', dim = false, approx = false }) {
  const entries = Object.entries(totalsByCurrency).sort((a, b) => b[1] - a[1]);
  const color = dim ? 'redesign.text.muted' : 'redesign.heading';
  return (
    <Box textAlign={align}>
      {entries.length === 0 ? (
        <Figure size="md" code="$" amount="0" color={color} display="block" />
      ) : (
        entries.map(([currency, amount]) => (
          <Box key={currency} whiteSpace="nowrap">
            {approx && <Text as="span" fontSize="md" mr={1} color="redesign.text.muted">≈</Text>}
            <Figure size="md" code={currency} amount={amount.toLocaleString('en-US', { maximumFractionDigits: 0 })} color={color} />
          </Box>
        ))
      )}
      <Text fontSize={{ base: 'xs', md: 'sm' }} color="redesign.text.muted" mt={1}>{label}</Text>
    </Box>
  );
}

function DonutChartGroup({ donutsByCurrency }) {
  const { t } = useTranslation('categories');
  if (donutsByCurrency.length === 0) {
    return <DonutChart segments={[]} size={100} />;
  }
  if (donutsByCurrency.length === 1) {
    return <DonutChart segments={donutsByCurrency[0].segments} size={100} />;
  }
  return (
    <Flex direction="column" align="center" justify="center" w="120px" h="100px">
      <Text fontSize="xs" color="redesign.text.muted" textAlign="center" lineHeight={1.3}>
        {t('summary.pickCurrency')}
      </Text>
    </Flex>
  );
}

function DonutChart({ segments, size = 100 }) {
  const circumference = 2 * Math.PI * 38;
  const hasSegments = segments.length > 0;
  const [hovered, setHovered] = useState(null);
  const { colorMode } = useColorMode();
  // The hovered slice's label sits on a tint of its category's colour.
  const hoveredTint = hovered && categoryTint(hovered.color || FALLBACK_CATEGORY_COLOR, colorMode);
  const GAP = 3;
  let offset = 0;
  return (
    <Box position="relative" display="inline-block">
      <Box as="svg" viewBox="0 0 100 100" w={`${size}px`} h={`${size}px`} transform="rotate(-90deg)" onMouseLeave={() => setHovered(null)}>
        <circle cx="50" cy="50" r="38" fill="none" stroke={chartColor('redesign.bg.sunken')} strokeWidth="12" />
        {hasSegments && segments.map(s => {
          const pct = s.share * circumference;
          const dashLength = Math.max(pct - GAP, 1);
          const dash = `${dashLength} ${circumference}`;
          const currentOffset = offset;
          offset += pct;
          return (
            <circle
              key={s.id}
              cx="50" cy="50" r="38"
              fill="none"
              stroke={s.color || FALLBACK_CATEGORY_COLOR}
              strokeWidth="12"
              strokeDasharray={dash}
              strokeDashoffset={`-${currentOffset}`}
              strokeLinecap="round"
              onMouseEnter={() => setHovered(s)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
      </Box>
      {hovered && (
        <Box
          position="absolute"
          top="100%"
          left="50%"
          transform="translateX(-50%)"
          mt={2}
          px={3}
          py={1}
          bg={hoveredTint.bg}
          color={hoveredTint.fg}
          borderRadius="full"
          fontSize="xs"
          fontWeight={600}
          whiteSpace="nowrap"
          pointerEvents="none"
          zIndex={5}
          boxShadow="md"
        >
          {hovered.icon || ''} {hovered.name} — {(hovered.share * 100).toFixed(1)}%
        </Box>
      )}
    </Box>
  );
}

const SEMAPHORE_GREEN = 'redesign.accent.green';
const SEMAPHORE_AMBER = 'redesign.accent.amber';
const SEMAPHORE_RED = 'redesign.accent.red';

function getSemaphoreColor(ratio) {
  if (ratio >= 1) return SEMAPHORE_RED;
  if (ratio >= 0.75) return SEMAPHORE_AMBER;
  return SEMAPHORE_GREEN;
}

function formatAmount(currency, amount) {
  const num = amount.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return { currency: currency || '', num };
}

function CategoryRow({ row, isSelected, onClick, approx = false }) {
  const color = row.color || FALLBACK_CATEGORY_COLOR;
  const { colorMode } = useColorMode();
  const tint = categoryTint(color, colorMode);
  const hasAvg = row.sixMonthAvg > 0;
  const ratio = hasAvg ? row.spent / row.sixMonthAvg : 0;
  const barWidth = hasAvg ? Math.min(ratio * 100, 100) : 0;
  const barColor = hasAvg ? getSemaphoreColor(ratio) : FALLBACK_CATEGORY_COLOR;
  const overPct = ratio > 1 ? Math.round((ratio - 1) * 100) : 0;
  const spent = formatAmount(row.currency, row.spent);
  const avg = formatAmount(row.currency, row.sixMonthAvg);
  return (
    <Box
      as="button"
      w="100%"
      display="flex"
      alignItems="center"
      px={1}
      py="4px"
      borderRadius="lg"
      bg={isSelected ? "redesign.bg.accent" : "transparent"}
      _hover={{ bg: isSelected ? "redesign.bg.accent" : "redesign.bg.hover" }}
      cursor="pointer"
      onClick={() => onClick(row)}
      textAlign="left"
    >
      <Flex
        align="center"
        gap={{ base: 1, md: 2 }}
        flex={{ base: 1, md: '0 0 auto' }}
        w={{ md: '240px' }}
        minW={0}
      >
        <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} display={{ base: 'none', md: 'block' }} />
        <Box
          px={{ base: 0.5, md: 2 }}
          py={0.5}
          borderRadius="md"
          bg={tint.bg}
          minW={{ base: '14px', md: '28px' }}
          textAlign="center"
          flexShrink={0}
        >
          <Text fontSize="2xs" fontWeight="600" color={tint.fg}>{row.count || 0}</Text>
        </Box>
        <Text fontSize={{ base: 'sm', md: 'lg' }} w={{ base: '16px', md: '28px' }} textAlign="center" flexShrink={0}>
          {row.icon || '📊'}
        </Text>
        <Text fontSize="sm" color="redesign.heading" noOfLines={1} isTruncated>{row.name}</Text>
      </Flex>

      <Flex
        w={{ base: '105px', md: '140px' }}
        justify="flex-end"
        align="baseline"
        gap={1}
        flexShrink={0}
      >
        {overPct > 0 && (
          <Text fontSize="2xs" fontWeight="600" color={SEMAPHORE_RED}>+{overPct}%</Text>
        )}
        <Text fontSize="sm" fontWeight="600" color="redesign.heading">
          {approx && row.spent > 0 && <Text as="span" mr={1} color="redesign.text.muted">≈</Text>}
          {spent.currency && <Text as="span" fontSize="2xs" mr={1} color="redesign.text.muted">{spent.currency}</Text>}
          {spent.num}
        </Text>
      </Flex>

      <Box
        flex={1}
        mx={{ base: 1, md: 4 }}
        h="7px"
        borderRadius="full"
        overflow="hidden"
        position="relative"
        bg="redesign.bg.card"
        minW={{ base: '24px', md: 'auto' }}
      >
        {barWidth > 0 && (
          <Box position="absolute" top={0} left={0} bottom={0} w={`${barWidth}%`} borderRadius="full" bg={barColor} />
        )}
      </Box>

      <Text
        fontSize={{ base: 'xs', md: 'sm' }}
        fontWeight="500"
        color="redesign.text.muted"
        w={{ base: '75px', md: '120px' }}
        textAlign="right"
        flexShrink={0}
      >
        {hasAvg ? (
          <>
            {avg.currency && <Text as="span" fontSize="2xs" mr={1}>{avg.currency}</Text>}
            {avg.num}
          </>
        ) : (
          <Text as="span" color="redesign.text.faint">—</Text>
        )}
      </Text>
    </Box>
  );
}

// The phone gets its own read-only page; the desktop body stays as it was.
export default function CategoriesPage(props) {
  return useIsPhone() ? <PhoneCategories {...props} /> : <CategoriesPageDesktop {...props} />;
}

function CategoriesPageDesktop() {
  const { t } = useTranslation('categories');
  const { can } = useWorkspace();
  const canCreate = can('category.create');
  const canUpdate = can('category.update');
  const canDelete = can('category.delete');
  const [selectedCategoryId, setSelectedCategoryId] = useUrlSyncedId('category_id', {
    fallback: () => window.matchMedia('(min-width: 768px)').matches ? PANEL_ALL_MODE : null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('expenses');
  const [showRegularCategories, setShowRegularCategories] = useState(true);
  const [showOtherCategories, setShowOtherCategories] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const toast = useToast();
  const isPanelOpen = selectedCategoryId !== null;
  // Same drag-to-size panel as the Movements page.
  const panelResize = useResizablePanel(isPanelOpen, pagePanelOptions(440));

  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [allTags, setAllTags] = useState([]);
  // MI-298: when set, every view on this page is derived from converted buckets.
  const { displayCurrency } = useDisplayCurrency();

  useEffect(() => {
    fetchTags().then(setAllTags).catch(() => {});
  }, []);

  const {
    loading, error, refetch,
    categories,
    tabCurrencies,
    effectiveCurrency,
    filteredRows,
    rowsWithSpending, rowsByCurrency, rowsWithoutSpending,
    donutsByCurrency,
    totalsByCurrency, sixMoAvgByCurrency, sixMoAvgByCatCurrency,
    totalsConverted, sixMoConverted,
    currentMonthName, monthDateRange,
    monthlyTimeline, yearlyMetrics,
    numUnconvertible,
    unconvertibleBuckets,
  } = useCategoriesView({ typeFilter, selectedCurrency, searchTerm, displayCurrency });

  const handleCurrencyTabChange = useCallback((currency) => {
    setSelectedCurrency(currency);
  }, []);

  // MI-311: the timeline's selected month lives on the page, not inside the
  // panels, so picking a category (or going back to the overview) keeps the
  // month the user was looking at. Stored as a year-month key rather than an
  // index — the timeline is rebuilt whenever the type or currency filter
  // changes, and an index would then point at a different month.
  // MI-346: it is kept in the address (`&month=2026-08`), so a link from the
  // chat opens a category on the month its answer was about, and a reload or
  // a shared link keeps it.
  const [monthParam, setMonthParam] = useUrlSyncedId('month', { type: 'string' });
  const selectedMonthKey = useMemo(() => {
    const month = fromMonthParam(monthParam);
    return month ? `${month.year}-${month.month}` : null;
  }, [monthParam]);

  const currentMonthIdx = useMemo(() => {
    if (!monthlyTimeline || monthlyTimeline.length === 0) return -1;
    const now = new Date();
    return monthlyTimeline.findIndex(
      (m) => m.year === now.getFullYear() && m.month === now.getMonth() + 1
    );
  }, [monthlyTimeline]);

  const selectedMonthIdx = useMemo(() => {
    if (!monthlyTimeline || monthlyTimeline.length === 0) return -1;
    if (selectedMonthKey) {
      const idx = monthlyTimeline.findIndex((m) => `${m.year}-${m.month}` === selectedMonthKey);
      if (idx >= 0) return idx;
    }
    return currentMonthIdx >= 0 ? currentMonthIdx : monthlyTimeline.length - 1;
  }, [monthlyTimeline, selectedMonthKey, currentMonthIdx]);

  const handleSelectMonth = useCallback((idx) => {
    const month = monthlyTimeline?.[idx];
    setMonthParam(month ? toMonthParam(month.year, month.month) : null);
  }, [monthlyTimeline, setMonthParam]);

  const handleCreateCategory = () => setSelectedCategoryId(PANEL_CREATE_MODE);
  const handleEditCategory = (category) => setSelectedCategoryId(category.id);
  const handleClosePanel = () => setSelectedCategoryId(null);
  const handleCategoryUpdated = () => refetch();

  const handleDeleteCategory = async (category) => {
    try {
      await deleteCategory(category.id, true);
      setCategoryToDelete(category);
      onDeleteOpen();
    } catch (err) {
      let errorMessage = t('toasts.deleteFailed');
      if (err.response?.status === 409) {
        const detail = err.response.data?.detail || '';
        const m = detail.match(/(\d+) movement/);
        errorMessage = m
          ? t('toasts.associatedCount', { count: parseInt(m[1], 10) })
          : t('toasts.associatedSome');
      } else if (err.response?.status === 404) {
        errorMessage = t('toasts.notFound');
      }
      toast({ title: t('toasts.errorTitle'), description: errorMessage, status: 'error', duration: 7000, isClosable: true });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteCategory(categoryToDelete.id);
      toast({ title: t('toasts.successTitle'), description: t('toasts.deleted'), status: 'success', duration: 3000, isClosable: true });
      onDeleteClose();
      setCategoryToDelete(null);
      if (selectedCategoryId === categoryToDelete.id) setSelectedCategoryId(null);
      await refetch();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('toasts.deleteFailed');
      toast({ title: t('toasts.errorTitle'), description: errorMessage, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedCategory = filteredRows.find(r => r.id === selectedCategoryId);

  return (
    <Flex ref={panelResize.containerRef} h={{ base: "calc(100vh - 56px)", md: "var(--mk-page-h)" }} overflow="hidden" position="relative">
      {/* Left: Categories List */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minW={0}
      >
        {/* Header */}
        <PageHeader title={t('title')}>
          <Flex align="center" gap={2}>
            {/* MI-298: global "display in" control — the ONLY selector on this
                page; the detail panels just react to it. */}
            <DisplayCurrencySelector />
            {tabCurrencies.length > 1 && (
              <DashboardCurrencyTabs
                currencies={tabCurrencies}
                selected={selectedCurrency}
                onChange={handleCurrencyTabChange}
              />
            )}
            {canCreate && (
              <IconButton
                icon={<AddIcon />}
                onClick={handleCreateCategory}
                aria-label={t('newAria')}
                size="sm"
                variant="ghost"
                color="redesign.text.muted"
                _hover={{ color: "redesign.heading", bg: "redesign.bg.card" }}
              />
            )}
          </Flex>
        </PageHeader>

        {/* Scrollable content */}
        <Box flex={1} overflowY="auto" px={{ base: 3, md: 5 }} py={5}>
          {/* Summary card with donut */}
          <Flex
            as="button"
            type="button"
            w="100%"
            textAlign="left"
            {...cardSurface}
            borderColor={selectedCategoryId === PANEL_ALL_MODE ? 'redesign.text.muted' : 'redesign.border'}
            p={{ base: 3, md: 6 }}
            mb={5}
            align="center"
            justify="space-between"
            cursor="pointer"
            transition="border-color 0.15s, box-shadow 0.15s"
            _hover={{ borderColor: 'redesign.text.muted' }}
            onClick={() => setSelectedCategoryId(PANEL_ALL_MODE)}
          >
            <CurrencyTotalsBlock
              totalsByCurrency={totalsByCurrency}
              approx={Boolean(displayCurrency) && totalsConverted}
              label={typeFilter === 'income'
                ? t('summary.earnedIn', { month: currentMonthName })
                : t('summary.spentIn', { month: currentMonthName })}
            />
            <DonutChartGroup donutsByCurrency={donutsByCurrency} />
            <CurrencyTotalsBlock
              totalsByCurrency={sixMoAvgByCurrency}
              approx={Boolean(displayCurrency) && sixMoConverted}
              label={t('summary.sixMonthAvg')}
              align="right"
              dim
            />
          </Flex>

          {/* MI-298: movements the conversion couldn't reach are disclosed, never
              silently dropped from a total the user reads as complete. */}
          {displayCurrency && numUnconvertible > 0 && (
            <Box mt={-3} mb={4}>
              <UnconvertibleNote
                buckets={unconvertibleBuckets}
                count={numUnconvertible}
              />
            </Box>
          )}

          {/* Filters */}
          <Flex justify="space-between" align="center" mb={4} gap={3}>
            <Flex
              bg="redesign.bg.sunken"
              border="1px solid"
              borderColor="redesign.border"
              p="3px"
              borderRadius="full"
              gap="2px"
            >
              {[
                { value: 'expenses', label: t('filters.expenses'), activeColor: 'redesign.amount.expense' },
                { value: 'income', label: t('filters.income'), activeColor: 'redesign.amount.income' },
              ].map(({ value, label, activeColor }) => {
                const isActive = typeFilter === value;
                return (
                  <Box
                    key={value}
                    as="button"
                    onClick={() => setTypeFilter(value)}
                    px={4}
                    py="5px"
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight={isActive ? 'semibold' : 'medium'}
                    bg={isActive ? 'redesign.bg.raised' : 'transparent'}
                    color={isActive ? activeColor : 'redesign.text.muted'}
                    boxShadow={isActive ? 'sm' : 'none'}
                    transition="all 0.15s"
                    _hover={!isActive ? { color: 'redesign.text' } : {}}
                  >
                    {label}
                  </Box>
                );
              })}
            </Flex>

            <InputGroup size="sm" maxW="250px">
              <InputLeftElement pointerEvents="none" h="32px">
                <SearchIcon color="redesign.text.muted" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder={t('filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="md"
                fontSize="sm"
                h="32px"
              />
            </InputGroup>
          </Flex>

          {/* Column headers */}
          <Flex align="center" mb={2} px={1}>
            <Flex
              as="button"
              align="center"
              gap={2}
              flex={{ base: 1, md: '0 0 auto' }}
              w={{ md: '240px' }}
              minW={{ base: 0, md: '240px' }}
              onClick={() => setShowRegularCategories(s => !s)}
              cursor="pointer"
              _hover={{ color: 'redesign.heading' }}
            >
              {showRegularCategories
                ? <ChevronDownIcon color="redesign.text.muted" boxSize={4} />
                : <ChevronRightIcon color="redesign.text.muted" boxSize={4} />}
              <Text textStyle="microLabel">
                {t('columns.regularCategories')}
              </Text>
            </Flex>
            <Text textStyle="microLabel" w={{ base: '105px', md: '140px' }} textAlign="right">
              {t('columns.spent')}
            </Text>
            <Box flex={1} />
            <Text
              textStyle="microLabel"
              w={{ base: '75px', md: '120px' }}
              textAlign="right"
            >
              {t('columns.sixMoAvg')}
            </Text>
          </Flex>

          {/* Category rows */}
          {loading ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="redesign.text.muted" />
                <Text color="redesign.text">{t('loading')}</Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center py={20}>
              <Text color="redesign.accent.red">{error}</Text>
            </Center>
          ) : filteredRows.length === 0 ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Text fontSize="xl" color="redesign.text.muted">
                  {searchTerm ? t('empty.withSearch') : t('empty.noCategories')}
                </Text>
                {!searchTerm && canCreate && (
                  <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreateCategory}>
                    {t('empty.createButton')}
                  </Button>
                )}
              </VStack>
            </Center>
          ) : (
            <>
              {showRegularCategories && (
                rowsByCurrency.length > 1 ? (
                  <VStack spacing={3} align="stretch">
                    {rowsByCurrency.map(({ currency, rows }) => (
                      <Box key={currency}>
                        <Flex align="center" gap={2} mb={1}>
                          <Text fontSize="2xs" fontWeight={600} color="redesign.text.muted" bg="redesign.bg.card" px={2} py="2px" borderRadius="md">
                            {currency}
                          </Text>
                          <Box flex={1} h="1px" bg="redesign.border" />
                        </Flex>
                        <VStack spacing={0} align="stretch">
                          {rows.map((row) => (
                            <CategoryRow
                              key={`${row.id}::${row.currency}`}
                              row={row}
                              approx={Boolean(displayCurrency) && row.isConverted}
                              isSelected={selectedCategoryId === row.id}
                              onClick={(r) => handleEditCategory(r)}
                            />
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {rowsWithSpending.map((row) => (
                      <CategoryRow
                        key={`${row.id}::${row.currency}`}
                        row={row}
                        approx={Boolean(displayCurrency) && row.isConverted}
                        isSelected={selectedCategoryId === row.id}
                        onClick={(r) => handleEditCategory(r)}
                      />
                    ))}
                  </VStack>
                )
              )}

              {rowsWithoutSpending.length > 0 && (
                <Box mt={4}>
                  <Flex
                    as="button"
                    align="center"
                    gap={2}
                    px={1}
                    py={1}
                    cursor="pointer"
                    onClick={() => setShowOtherCategories(s => !s)}
                    _hover={{ color: 'redesign.heading' }}
                  >
                    {showOtherCategories
                      ? <ChevronDownIcon color="redesign.text.muted" boxSize={4} />
                      : <ChevronRightIcon color="redesign.text.muted" boxSize={4} />}
                    <Text textStyle="microLabel">
                      {t('columns.otherCategories')}
                    </Text>
                    <Text textStyle="microLabel">
                      ({rowsWithoutSpending.length})
                    </Text>
                  </Flex>

                  {showOtherCategories && (
                    <VStack spacing={0} align="stretch" mt={1}>
                      {rowsWithoutSpending.map((row) => (
                        <CategoryRow
                          key={`${row.id}::${row.currency || 'none'}`}
                          row={row}
                          approx={Boolean(displayCurrency) && row.isConverted}
                          isSelected={selectedCategoryId === row.id}
                          onClick={(r) => handleEditCategory(r)}
                        />
                      ))}
                    </VStack>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Right: Detail Panel (always visible on md+) */}
      <Box
        display={{ base: isPanelOpen ? "block" : "none", md: "block" }}
        position={{ base: "absolute", md: "relative" }}
        right={{ base: 0, md: "auto" }}
        top={{ base: 0, md: "auto" }}
        w={{ base: "100%", md: resizablePanelWidth("44%", 440), lg: resizablePanelWidth("42%", 440) }}
        h="100%"
        bg="redesign.bg.surface"
        borderLeft="1px solid"
        borderLeftColor="redesign.border"
        boxShadow={{ base: "-2px 0 8px rgba(0,0,0,0.15)", md: "none" }}
        zIndex={{ base: 10, md: "auto" }}
      >
        <PanelResizeHandle resize={panelResize} />
        {selectedCategoryId === PANEL_ALL_MODE ? (
          <AllCategoriesDetailPanel
            categories={categories}
            monthlyTimeline={monthlyTimeline}
            yearlyMetrics={yearlyMetrics}
            sixMoAvgByCurrency={sixMoAvgByCurrency}
            sixMoAvgByCatCurrency={sixMoAvgByCatCurrency}
            currentMonthName={currentMonthName}
            typeFilter={typeFilter}
            selectedMonthIdx={selectedMonthIdx}
            currentMonthIdx={currentMonthIdx}
            onSelectMonth={handleSelectMonth}
            onClose={handleClosePanel}
            onCategorySelect={(id) => setSelectedCategoryId(id)}
          />
        ) : isPanelOpen ? (
          <CategoryDetailPanel
            categoryId={selectedCategoryId === PANEL_CREATE_MODE ? null : selectedCategoryId}
            category={selectedCategory}
            categories={categories}
            allTags={allTags}
            onClose={handleClosePanel}
            onCategoryUpdated={canUpdate ? handleCategoryUpdated : undefined}
            onDelete={canDelete ? handleDeleteCategory : undefined}
            monthlyTimeline={monthlyTimeline}
            yearlyMetrics={yearlyMetrics}
            dateRange={monthDateRange}
            typeFilter={typeFilter}
            selectedMonthIdx={selectedMonthIdx}
            currentMonthIdx={currentMonthIdx}
            onSelectMonth={handleSelectMonth}
            effectiveCurrency={effectiveCurrency}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap={3} h="100%" bg="redesign.bg.page" color="redesign.text.faint">
            <Box as={LuLayers} boxSize="32px" />
            <Text fontSize="sm" color="redesign.text.muted">{t('selectToView')}</Text>
          </Flex>
        )}
      </Box>

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        message={categoryToDelete ? t('deleteDialog.message', { name: categoryToDelete.name }) : ''}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={deleteLoading}
      />
    </Flex>
  );
}
